import { BASE_BULLMQ_CONFIG } from "@okf/ootils";

/**
 * Builds a synthetic segByAutoAnnotateIdJobs payload that mirrors the shape
 * produced by okf-sub's SocketIOService.calculateSegByAutoAnnotateIdJobs, so
 * AutoAnnotateProgressFeedback can render every visual state without a real
 * auto-annotate run.
 *
 * Used internally by AutoAnnotateProgressFeedback when its `testMode` prop
 * is set.
 *
 * Recognised states:
 *   - "annotating"              → 50% done, all pending docs still in flight
 *   - "annotatingWithFailures"  → 45% completed + 5% failed, rest pending
 *   - "completed"               → 100% completed, no failures
 *   - "completedWithFailures"   → 90% completed + 10% failed (terminal)
 *
 * Usage — single run:
 *   <AutoAnnotateProgressFeedback
 *     testMode={{ contentType: "anything", state: "annotating", tplTitle: "Articles" }}
 *   />
 *
 * Usage — multiple parallel runs (one card per entry):
 *   <AutoAnnotateProgressFeedback
 *     testMode={[
 *       { contentType: "a", state: "annotating",              tplTitle: "Articles" },
 *       { contentType: "b", state: "completed",               tplTitle: "Users" },
 *       { contentType: "c", state: "completedWithFailures",   tplTitle: "Posts" },
 *     ]}
 *   />
 */

// Helper: synthesize N docs with titles "<base> 1", "<base> 2", …
const makeDocs = (base, count, startIdx = 1) =>
  Array.from({ length: count }, (_, i) => ({
    _id: `synthetic-${base}-${startIdx + i}`,
    title: `${base} ${startIdx + i}`,
  }));

const buildOneRun = ({ contentType, state, tplTitle }, idx = 0) => {
  // Each synthetic run is sized to 20 docs so the expanded list is easy
  // to eyeball but still exercises overflow/scroll.
  const TOTAL = 20;

  const baseQueueShape = (overrides) => ({
    queueName: BASE_BULLMQ_CONFIG.AI_AUTO_ANNOTATE_QUEUE.id,
    totalItems: TOTAL,
    completed: { count: 0, percent: 0 },
    failed: { count: 0, percent: 0 },
    waiting: { count: 0, percent: 0 },
    active: { count: 0, percent: 0 },
    delayed: { count: 0, percent: 0 },
    actualPercentCompleted: 0,
    status: "autoAnnotating",
    ...overrides,
  });

  let queueData;
  let docs;

  if (state === "completed") {
    queueData = baseQueueShape({
      completed: { count: TOTAL, percent: 100 },
      actualPercentCompleted: 100,
      status: "autoAnnotateCompleted",
    });
    docs = {
      completed: makeDocs("Doc", TOTAL),
      failed: [],
      pending: [],
    };
  } else if (state === "completedWithFailures") {
    const okCount = Math.round(TOTAL * 0.9);
    const failCount = TOTAL - okCount;
    queueData = baseQueueShape({
      completed: { count: okCount, percent: (okCount / TOTAL) * 100 },
      failed: { count: failCount, percent: (failCount / TOTAL) * 100 },
      actualPercentCompleted: 100,
      status: "autoAnnotateCompleted",
    });
    docs = {
      completed: makeDocs("Doc", okCount),
      failed: makeDocs("Failed Doc", failCount, okCount + 1),
      pending: [],
    };
  } else if (state === "preparing") {
    // Prep phase: the run's only visible job is the prep job, every doc
    // pending, nothing annotated yet.
    queueData = baseQueueShape({
      queueName: BASE_BULLMQ_CONFIG.AI_AUTO_ANNOTATE_PREP_QUEUE.id,
      totalItems: 1,
      active: { count: 1, percent: 100 },
      actualPercentCompleted: 0,
      status: "autoAnnotating",
      phase: "preparing",
    });
    docs = {
      completed: [],
      failed: [],
      pending: makeDocs("Doc", TOTAL),
    };
  } else if (state === "annotatingWithFailures") {
    const okCount = Math.round(TOTAL * 0.45);
    const failCount = Math.round(TOTAL * 0.05);
    const pendingCount = TOTAL - okCount - failCount;
    queueData = baseQueueShape({
      completed: { count: okCount, percent: (okCount / TOTAL) * 100 },
      failed: { count: failCount, percent: (failCount / TOTAL) * 100 },
      active: { count: pendingCount, percent: (pendingCount / TOTAL) * 100 },
      actualPercentCompleted: (okCount / TOTAL) * 100,
      status: "autoAnnotating",
    });
    docs = {
      completed: makeDocs("Doc", okCount),
      failed: makeDocs("Failed Doc", failCount, okCount + 1),
      pending: makeDocs("Doc", pendingCount, okCount + failCount + 1),
    };
  } else {
    // default: "annotating"
    const okCount = Math.round(TOTAL * 0.5);
    const pendingCount = TOTAL - okCount;
    queueData = baseQueueShape({
      completed: { count: okCount, percent: (okCount / TOTAL) * 100 },
      active: { count: pendingCount, percent: (pendingCount / TOTAL) * 100 },
      actualPercentCompleted: (okCount / TOTAL) * 100,
      status: "autoAnnotating",
    });
    docs = {
      completed: makeDocs("Doc", okCount),
      failed: [],
      pending: makeDocs("Doc", pendingCount, okCount + 1),
    };
  }

  const autoAnnotateId = `synthetic-${state}-${contentType}`;
  return {
    autoAnnotateId,
    contentType,
    autoAnnotateTotalDocCount: TOTAL,
    // unique per entry so the (most-recent-first) sort produces a stable order
    activityDate: new Date(Date.now() - idx * 1000).toISOString(),
    queues: { [queueData.queueName]: queueData },
    docs,
    // embedded so SingleRunCard can pick a per-card tplTitle override
    _testTplTitle: tplTitle,
  };
};

export const buildSyntheticSegByAutoAnnotateIdJobs = (testMode) => {
  const entries = Array.isArray(testMode) ? testMode : [testMode];
  const result = {};
  entries.forEach((entry, idx) => {
    const run = buildOneRun(entry, idx);
    result[run.autoAnnotateId] = run;
  });
  return result;
};
